import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Target,
  Activity,
  TrendingDown,
  TrendingUp,
  Scale,
  ArrowLeft,
  Dumbbell,
  Flame,
  Zap,
  BarChart2,
  CheckCircle,
  Info,
  ChevronRight,
  User
} from 'lucide-react';
import foodTrackingService, { CalorieGoals } from '../services/foodTrackingService';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Activity level descriptions with fitness context
const ACTIVITY_LEVELS = [
  {
    value: 'sedentary',
    label: 'Sedentar',
    description: 'Birou, fără exerciții',
    icon: '🪑',
    multiplier: '×1.2',
    color: 'border-gray-200 hover:border-gray-300'
  },
  {
    value: 'light',
    label: 'Ușor Activ',
    description: '1-3 antrenamente/săptămână',
    icon: '🚶',
    multiplier: '×1.375',
    color: 'border-blue-200 hover:border-blue-300'
  },
  {
    value: 'moderate',
    label: 'Moderat Activ',
    description: '3-5 antrenamente/săptămână',
    icon: '🏃',
    multiplier: '×1.55',
    color: 'border-green-200 hover:border-green-300'
  },
  {
    value: 'active',
    label: 'Foarte Activ',
    description: '6-7 antrenamente/săptămână',
    icon: '💪',
    multiplier: '×1.725',
    color: 'border-orange-200 hover:border-orange-300'
  },
  {
    value: 'very_active',
    label: 'Atlet / Intensiv',
    description: 'Antrenament intens zilnic',
    icon: '🏋️',
    multiplier: '×1.9',
    color: 'border-red-200 hover:border-red-300'
  }
];

// Fitness goals with professional descriptions
const FITNESS_GOALS = [
  {
    value: 'lose',
    label: 'Slăbire Activă',
    emoji: '🔥',
    description: 'Deficit -500 kcal/zi',
    detail: '~0.5 kg/săptămână',
    macros: 'Proteină ↑ 35% | Carbs 35% | Grăsimi 30%',
    color: 'border-red-400 bg-red-50',
    activeColor: 'border-red-500 bg-red-50 shadow-red-100',
    textColor: 'text-red-700',
    icon: TrendingDown,
    iconColor: 'text-red-500'
  },
  {
    value: 'lose_slow',
    label: 'Slăbire Lentă',
    emoji: '🌿',
    description: 'Deficit -250 kcal/zi',
    detail: '~0.25 kg/săptămână',
    macros: 'Proteină 30% | Carbs 40% | Grăsimi 30%',
    color: 'border-orange-300 bg-orange-50',
    activeColor: 'border-orange-500 bg-orange-50 shadow-orange-100',
    textColor: 'text-orange-700',
    icon: TrendingDown,
    iconColor: 'text-orange-500'
  },
  {
    value: 'maintain',
    label: 'Menținere',
    emoji: '⚖️',
    description: 'Echilibru caloric',
    detail: 'Greutate stabilă',
    macros: 'Proteină 30% | Carbs 40% | Grăsimi 30%',
    color: 'border-blue-300 bg-blue-50',
    activeColor: 'border-blue-500 bg-blue-50 shadow-blue-100',
    textColor: 'text-blue-700',
    icon: Scale,
    iconColor: 'text-blue-500'
  },
  {
    value: 'gain_healthy',
    label: 'Creștere Sănătoasă',
    emoji: '🌱',
    description: 'Surplus +200 kcal/zi',
    detail: 'Greutate echilibrată',
    macros: 'Proteină 25% | Carbs 45% | Grăsimi 30%',
    color: 'border-teal-300 bg-teal-50',
    activeColor: 'border-teal-500 bg-teal-50 shadow-teal-100',
    textColor: 'text-teal-700',
    icon: TrendingUp,
    iconColor: 'text-teal-500'
  },
  {
    value: 'gain',
    label: 'Masă Musculară',
    emoji: '💪',
    description: 'Surplus +300 kcal/zi',
    detail: 'Hipertrofie musculară',
    macros: 'Proteină 30% | Carbs 45% | Grăsimi 25%',
    color: 'border-green-300 bg-green-50',
    activeColor: 'border-green-500 bg-green-50 shadow-green-100',
    textColor: 'text-green-700',
    icon: TrendingUp,
    iconColor: 'text-green-500'
  }
];

export default function CalorieGoalsSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [error, setError] = useState('');
  const [calculatedGoals, setCalculatedGoals] = useState<CalorieGoals | null>(null);
  const [existingGoals, setExistingGoals] = useState<CalorieGoals | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [age, setAge] = useState('');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [activityLevel, setActivityLevel] = useState<'sedentary' | 'light' | 'moderate' | 'active' | 'very_active'>('moderate');
  const [goal, setGoal] = useState<'lose' | 'maintain' | 'gain' | 'lose_slow' | 'gain_healthy'>('maintain');

  // BMR preview calculation
  const [bmrPreview, setBmrPreview] = useState<number | null>(null);
  const [tdeePreview, setTdeePreview] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const goals = await foodTrackingService.getGoals();
        setExistingGoals(goals);
      } catch (error) {
        setShowForm(true);
      }

      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(`${API_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const profile = response.data;

        if (profile.birthDate) {
          const birthDate = new Date(profile.birthDate);
          const today = new Date();
          let calculatedAge = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            calculatedAge--;
          }
          setAge(calculatedAge.toString());
        }

        if (profile.gender) {
          setGender(profile.gender === 'female' ? 'female' : 'male');
        }

        if (profile.weight) {
          setWeight(profile.weight.toString());
        }

        if (profile.height) {
          setHeight(profile.height.toString());
        }
      } catch (error) {
        console.log('Could not load profile data');
      } finally {
        setLoadingExisting(false);
      }
    };

    loadData();
  }, []);

  // Live BMR/TDEE preview
  useEffect(() => {
    if (age && weight && height) {
      const w = parseFloat(weight);
      const h = parseFloat(height);
      const a = parseInt(age);
      if (w > 0 && h > 0 && a > 0) {
        // Mifflin-St Jeor
        const bmr = gender === 'male'
          ? 10 * w + 6.25 * h - 5 * a + 5
          : 10 * w + 6.25 * h - 5 * a - 161;
        setBmrPreview(Math.round(bmr));

        const multipliers: Record<string, number> = {
          sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, very_active: 1.9
        };
        setTdeePreview(Math.round(bmr * (multipliers[activityLevel] || 1.55)));
      }
    }
  }, [age, weight, height, gender, activityLevel]);

  const handleCalculate = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const goals = await foodTrackingService.calculateGoals({
        age: parseInt(age),
        gender,
        weight: parseFloat(weight),
        height: parseFloat(height),
        activityLevel,
        goal
      });

      setCalculatedGoals(goals);

      setTimeout(() => {
        const fromFoodTracking = location.state?.from === 'food-tracking';
        if (fromFoodTracking) {
          navigate('/food-tracking');
        } else {
          navigate('/dashboard');
        }
      }, 3000);
    } catch (err: any) {
      console.error('Goals calculation error:', err);
      setError(err.message || 'Eroare la calcularea obiectivelor. Verifică datele introduse.');
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const selectedGoalConfig = FITNESS_GOALS.find(g => g.value === goal);
  const selectedActivityConfig = ACTIVITY_LEVELS.find(a => a.value === activityLevel);

  if (loadingExisting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Se încarcă obiectivele tale...</p>
        </div>
      </div>
    );
  }

  // SUCCESS SCREEN
  if (calculatedGoals) {
    const goalConfig = FITNESS_GOALS.find(g => g.value === goal);
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Success header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-8 text-white text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold mb-2">Obiective Setate! 🎉</h1>
              <p className="text-white/85">Planul tău nutrițional fitness este gata</p>
            </div>

            <div className="p-8">
              {/* Goal badge */}
              {goalConfig && (
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6 ${goalConfig.activeColor} ${goalConfig.textColor} border-2`}>
                  <span className="text-lg">{goalConfig.emoji}</span>
                  {goalConfig.label}
                </div>
              )}

              {/* Main stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-5 border border-orange-100 text-center">
                  <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-1">Calorii Zilnice</p>
                  <p className="text-4xl font-bold text-orange-600">{Math.round(calculatedGoals.targetCalories)}</p>
                  <p className="text-xs text-gray-500 mt-1">kcal/zi</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100 text-center">
                  <Activity className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-1">TDEE</p>
                  <p className="text-4xl font-bold text-blue-600">{tdeePreview || Math.round(calculatedGoals.targetCalories)}</p>
                  <p className="text-xs text-gray-500 mt-1">metabolism total</p>
                </div>
              </div>

              {/* Macros */}
              <div className="bg-gray-50 rounded-2xl p-5 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart2 className="w-5 h-5 text-gray-600" />
                  Distribuție Macronutrienți
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-blue-100">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-sm font-bold text-blue-600">P</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{Math.round(calculatedGoals.targetProtein)}g</p>
                    <p className="text-xs text-gray-500 mt-1">Proteine</p>
                    <p className="text-xs text-blue-500 font-medium">Mușchi & recuperare</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-yellow-100">
                    <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-sm font-bold text-yellow-600">C</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-600">{Math.round(calculatedGoals.targetCarbs)}g</p>
                    <p className="text-xs text-gray-500 mt-1">Carbohidrați</p>
                    <p className="text-xs text-yellow-500 font-medium">Energie & performanță</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-orange-100">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-sm font-bold text-orange-600">G</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{Math.round(calculatedGoals.targetFats)}g</p>
                    <p className="text-xs text-gray-500 mt-1">Grăsimi</p>
                    <p className="text-xs text-orange-500 font-medium">Hormoni & sănătate</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                <span>Te redirecționăm în câteva secunde...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Înapoi la Dashboard</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl mb-5 shadow-xl">
            <Target className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Obiective Calorice Fitness
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Calculăm TDEE-ul tău (Total Daily Energy Expenditure) și distribuția optimă de macronutrienți pentru obiectivul tău fitness
          </p>
        </div>

        {/* Science badge */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-2">
            <Info className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">Formula Mifflin-St Jeor • Standard ISSN & ACSM</span>
          </div>
        </div>

        {/* Existing Goals Display */}
        {existingGoals && !showForm ? (
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl">
                  <Target className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Obiectivele Tale Actuale</h2>
                  <p className="text-white/80 text-sm">Plan nutrițional fitness activ</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Main calorie target */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 mb-5 border border-orange-100 text-center">
                <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-1">Țintă Calorică Zilnică</p>
                <p className="text-5xl font-bold text-orange-600">{Math.round(existingGoals.targetCalories)}</p>
                <p className="text-sm text-gray-500 mt-1">kcal/zi</p>
              </div>

              {/* Macros grid */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
                  <p className="text-xs text-gray-500 mb-1">Proteine</p>
                  <p className="text-2xl font-bold text-blue-600">{Math.round(existingGoals.targetProtein)}g</p>
                  <p className="text-xs text-blue-500 mt-1">Mușchi</p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 text-center border border-yellow-100">
                  <p className="text-xs text-gray-500 mb-1">Carbohidrați</p>
                  <p className="text-2xl font-bold text-yellow-600">{Math.round(existingGoals.targetCarbs)}g</p>
                  <p className="text-xs text-yellow-500 mt-1">Energie</p>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 text-center border border-orange-100">
                  <p className="text-xs text-gray-500 mb-1">Grăsimi</p>
                  <p className="text-2xl font-bold text-orange-600">{Math.round(existingGoals.targetFats)}g</p>
                  <p className="text-xs text-orange-500 mt-1">Hormoni</p>
                </div>
              </div>

              {/* Goal & Activity */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Obiectiv</p>
                  <p className="text-sm font-bold text-gray-900">
                    {existingGoals.goalType === 'weight_loss' ? '🔥 Slăbire Activă' :
                     existingGoals.goalType === 'weight_loss_slow' ? '🌿 Slăbire Lentă' :
                     existingGoals.goalType === 'muscle_gain' ? '💪 Masă Musculară' :
                     existingGoals.goalType === 'healthy_weight_gain' ? '🌱 Creștere Sănătoasă' :
                     '⚖️ Menținere'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Nivel Activitate</p>
                  <p className="text-sm font-bold text-gray-900 capitalize">
                    {existingGoals.activityLevel === 'sedentary' ? '🪑 Sedentar' :
                     existingGoals.activityLevel === 'light' ? '🚶 Ușor Activ' :
                     existingGoals.activityLevel === 'moderate' ? '🏃 Moderat Activ' :
                     existingGoals.activityLevel === 'active' ? '💪 Foarte Activ' :
                     '🏋️ Atlet'}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleBack}
                  className="flex-1 px-5 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-medium"
                >
                  Înapoi
                </button>
                <button
                  onClick={() => setShowForm(true)}
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center justify-center gap-2"
                >
                  <Target className="w-4 h-4" />
                  Recalculează
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* MAIN FORM */
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-6">
            {existingGoals && (
              <div className="bg-blue-50 border-b border-blue-100 p-4">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <Info className="w-4 h-4 flex-shrink-0" />
                  <span><strong>Recalculare:</strong> Completează formularul pentru a actualiza obiectivele tale calorice fitness.</span>
                </p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border-b border-red-100 p-4">
                <p className="text-red-700 text-sm font-medium flex items-center gap-2">
                  <span>⚠️</span> {error}
                </p>
              </div>
            )}

            <form onSubmit={handleCalculate} className="p-6 space-y-8">

              {/* SECTION 1: Personal Data */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <User className="w-5 h-5 text-orange-500" />
                    Date Antropometrice
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Gender */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sex Biologic <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-400 font-normal ml-2">(necesar pentru formula BMR)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setGender('male')}
                        className={`px-4 py-3 rounded-xl border-2 font-semibold transition-all flex items-center justify-center gap-2 ${
                          gender === 'male'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        👨 Masculin
                      </button>
                      <button
                        type="button"
                        onClick={() => setGender('female')}
                        className={`px-4 py-3 rounded-xl border-2 font-semibold transition-all flex items-center justify-center gap-2 ${
                          gender === 'female'
                            ? 'border-pink-500 bg-pink-50 text-pink-700 shadow-md'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        👩 Feminin
                      </button>
                    </div>
                  </div>

                  {/* Age */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Vârstă <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        required
                        min="15"
                        max="100"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all pr-12"
                        placeholder="ex: 28"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">ani</span>
                    </div>
                  </div>

                  {/* Weight */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Greutate <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        required
                        min="30"
                        max="300"
                        step="0.1"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all pr-10"
                        placeholder="ex: 75"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">kg</span>
                    </div>
                  </div>

                  {/* Height */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Înălțime <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        required
                        min="100"
                        max="250"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all pr-10"
                        placeholder="ex: 175"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">cm</span>
                    </div>
                  </div>

                  {/* Live BMR Preview */}
                  {bmrPreview && (
                    <div className="md:col-span-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-900">Preview Metabolism</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">BMR (metabolism bazal)</p>
                          <p className="text-xl font-bold text-blue-600">{bmrPreview} kcal</p>
                          <p className="text-xs text-gray-400">calorii în repaus complet</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">TDEE (metabolism total)</p>
                          <p className="text-xl font-bold text-indigo-600">{tdeePreview} kcal</p>
                          <p className="text-xs text-gray-400">cu activitate {selectedActivityConfig?.multiplier}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION 2: Activity Level */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-orange-500" />
                    Nivel de Activitate Fizică
                  </h3>
                </div>

                <div className="space-y-2">
                  {ACTIVITY_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setActivityLevel(level.value as any)}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                        activityLevel === level.value
                          ? 'border-orange-500 bg-orange-50 shadow-md'
                          : `${level.color} bg-white`
                      }`}
                    >
                      <span className="text-2xl flex-shrink-0">{level.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className={`font-semibold ${activityLevel === level.value ? 'text-orange-700' : 'text-gray-900'}`}>
                            {level.label}
                          </p>
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                            activityLevel === level.value ? 'bg-orange-200 text-orange-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {level.multiplier}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{level.description}</p>
                      </div>
                      {activityLevel === level.value && (
                        <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* SECTION 3: Fitness Goal */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Dumbbell className="w-5 h-5 text-orange-500" />
                    Obiectiv Fitness
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {FITNESS_GOALS.map((g) => {
                    const IconComponent = g.icon;
                    return (
                      <button
                        key={g.value}
                        type="button"
                        onClick={() => setGoal(g.value as any)}
                        className={`p-5 rounded-2xl border-2 transition-all text-left relative ${
                          goal === g.value
                            ? `${g.activeColor} shadow-lg scale-[1.02]`
                            : `${g.color} hover:shadow-md`
                        }`}
                      >
                        {goal === g.value && (
                          <div className="absolute top-3 right-3">
                            <CheckCircle className={`w-5 h-5 ${g.iconColor}`} />
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{g.emoji}</span>
                          <IconComponent className={`w-5 h-5 ${g.iconColor}`} />
                        </div>
                        <p className={`font-bold text-gray-900 mb-1`}>{g.label}</p>
                        <p className={`text-sm font-semibold ${g.textColor} mb-1`}>{g.description}</p>
                        <p className="text-xs text-gray-500 mb-2">{g.detail}</p>
                        <div className={`text-xs px-2 py-1 rounded-lg ${goal === g.value ? 'bg-white/60' : 'bg-white/80'} text-gray-600`}>
                          {g.macros}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Selected goal info */}
                {selectedGoalConfig && tdeePreview && (
                  <div className={`mt-4 p-4 rounded-xl border-2 ${selectedGoalConfig.activeColor}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{selectedGoalConfig.emoji}</span>
                      <p className={`font-semibold ${selectedGoalConfig.textColor}`}>
                        Estimare pentru {selectedGoalConfig.label}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-xs text-gray-500">TDEE</p>
                        <p className="font-bold text-gray-900">{tdeePreview} kcal</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Țintă</p>
                        <p className={`font-bold ${selectedGoalConfig.textColor}`}>
                          {goal === 'lose' ? tdeePreview - 500 :
                           goal === 'lose_slow' ? tdeePreview - 250 :
                           goal === 'gain' ? tdeePreview + 300 :
                           goal === 'gain_healthy' ? tdeePreview + 200 :
                           tdeePreview} kcal
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Diferență</p>
                        <p className={`font-bold ${selectedGoalConfig.textColor}`}>
                          {goal === 'lose' ? '-500' :
                           goal === 'lose_slow' ? '-250' :
                           goal === 'gain' ? '+300' :
                           goal === 'gain_healthy' ? '+200' :
                           '0'} kcal
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white text-lg font-bold rounded-2xl hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Se calculează...</span>
                    </>
                  ) : (
                    <>
                      <Target className="w-6 h-6" />
                      <span>Calculează & Salvează Obiectivele</span>
                      <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">Cum funcționează calculul?</p>
              <p className="text-xs text-blue-700 leading-relaxed">
                Folosim <strong>Formula Mifflin-St Jeor</strong> (cea mai precisă pentru adulți activi) pentru a calcula BMR-ul tău, 
                apoi aplicăm multiplicatorul de activitate pentru TDEE. Macronutrienții sunt distribuiți optim conform 
                standardelor <strong>ISSN</strong> (International Society of Sports Nutrition) pentru obiectivul tău.
              </p>
              <p className="text-xs text-blue-600 mt-2">
                💡 Poți modifica aceste obiective oricând din Setări → Obiective Calorice
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
